<template>
  <div class="map-container" :class="{ 'map-container--nav': navigationActive }" :style="containerStyle">
    <div ref="mapCanvas" class="map-canvas"></div>

    <!-- Driving mode: next maneuver banner -->
    <div v-if="navigationActive && currentStep" class="nav-banner">
      <v-icon large color="white" class="nav-banner-icon">{{ currentStepIcon }}</v-icon>
      <div class="nav-banner-text">
        <div class="nav-banner-distance" v-if="distanceToCurrentStepM > 0">
          {{ formatDistance(distanceToCurrentStepM) }}
        </div>
        <div class="nav-banner-instruction">{{ currentStepText }}</div>
      </div>
    </div>

    <!-- Off-route warning -->
    <div v-if="navigationActive && offRoute" class="off-route-panel">
      {{ $t('guiasMotorista.navOffRoute') }}
    </div>

    <!-- Simplified route warning -->
    <div v-if="simplifiedRouteWarning" class="notice-panel">
      {{ $t('guiasMotorista.navSimplifiedRouteWarning') }}
    </div>

    <!-- Preview / info -->
    <div class="info-panel" v-if="userCoords && !navigationActive">
      <p><b>{{ $t('guiasMotorista.navSpeed') }}:</b> {{ speed }} km/h</p>
    </div>

    <div v-if="navigationActive && userCoords" class="speed-chip">
      {{ speed }} km/h
    </div>

    <!-- Re-center FAB -->
    <v-btn
      v-if="navigationActive && userPannedAway"
      fab
      small
      color="white"
      class="recenter-fab"
      :title="$t('guiasMotorista.navRecenter')"
      @click="recenterMap"
    >
      <v-icon color="primary">mdi-crosshairs-gps</v-icon>
    </v-btn>

    <!-- All steps drawer (preview or collapsed during nav) -->
    <div
      v-if="routeInstructions.length && (!navigationActive || showAllSteps)"
      class="instructions-panel"
      :class="{ 'instructions-panel--nav': navigationActive }"
    >
      <div class="instructions-header">
        <p class="instructions-title">{{ $t('guiasMotorista.navHowToFollow') }}</p>
        <v-btn
          v-if="navigationActive"
          icon
          x-small
          @click="showAllSteps = false"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
      <ol class="instructions-list">
        <li
          v-for="(step, i) in routeInstructions"
          :key="i"
          class="instruction-row"
          :class="{
            'instruction-row--clickable': stepHasMapPoint(step),
            'instruction-row--active': currentStepIndex === i,
          }"
          @click="focusInstructionStep(step, i)"
        >
          <v-icon x-small class="instruction-icon">{{ stepIcon(step) }}</v-icon>
          <span class="instruction-text">{{ stepText(step) }}</span>
          <span v-if="step.distanceToNextM > 0" class="instruction-meta">
            {{ formatDistance(step.distanceToNextM) }}
          </span>
        </li>
      </ol>
    </div>

    <v-btn
      v-if="navigationActive && routeInstructions.length && !showAllSteps"
      small
      outlined
      color="white"
      class="all-steps-btn"
      @click="showAllSteps = true"
    >
      {{ $t('guiasMotorista.navAllSteps') }}
    </v-btn>

    <!-- Dev-only: simulate driving along the registered polyline -->
    <div v-if="showSimPanel" class="sim-panel">
      <div class="sim-panel-title">Dev · simulate GPS</div>
      <div class="sim-panel-row">
        <v-btn icon x-small dark @click="toggleSimPlay()" :title="simPlaying ? 'Pause' : 'Play'">
          <v-icon small>{{ simPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
        </v-btn>
        <v-btn icon x-small dark @click="resetSim" title="Reset to start">
          <v-icon small>mdi-replay</v-icon>
        </v-btn>
        <span class="sim-panel-speed">{{ simSpeedKmh }} km/h</span>
      </div>
      <v-slider
        v-model="simSpeedKmh"
        class="sim-slider"
        dense
        hide-details
        dark
        min="5"
        max="80"
        step="5"
        @change="onSimSpeedChange"
      />
      <v-slider
        :value="simProgressPct"
        class="sim-slider"
        dense
        hide-details
        dark
        min="0"
        max="100"
        step="0.5"
        @change="onSimSeek"
      />
      <div class="sim-panel-hint">
        {{ simProgressPct.toFixed(0) }}% · {{ simDistanceLabel }}
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  buildNavigationPlan,
  projectPointOnPolyline,
  findCurrentStepIndex,
  getRemainingPolyline,
  formatDistanceMeters,
  maneuverIcon,
  OFF_ROUTE_M,
  SNAP_TO_LINE_M,
} from '@/utils/routeNavigation';
import { createRouteNavigationSimulator, polylineTotalLengthM } from '@/utils/routeNavigationSimulator';
import { IsDevEnv } from '@/constants';

export default {
  name: 'MapaMotorista',
  props: {
    customRoute: {
      type: Array,
      required: true,
    },
    stops: {
      type: Array,
      default: () => [],
    },
    mapHeight: {
      type: String,
      default: '500px',
    },
    navigationActive: {
      type: Boolean,
      default: false,
    },
    vehiclePosition: {
      type: Object,
      default: null,
    },
    defaultCenter: {
      type: Array,
      default: null,
    },
  },
  computed: {
    mapDefaultCenter() {
      const fromProp = this.defaultCenter;
      if (fromProp && fromProp.length >= 2) {
        const lat = Number(fromProp[0]);
        const lng = Number(fromProp[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
      }
      const c = this.$store && this.$store.getters ? this.$store.getters['config/centerDefault'] : null;
      if (c && c.length >= 2) {
        const lat = Number(c[0]);
        const lng = Number(c[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
      }
      return [0, 0];
    },
    containerStyle() {
      return { height: this.mapHeight };
    },
    currentStep() {
      if (!this.routeInstructions.length) return null;
      const idx = Math.min(this.currentStepIndex, this.routeInstructions.length - 1);
      return this.routeInstructions[idx] || null;
    },
    currentStepText() {
      return this.currentStep ? this.stepText(this.currentStep) : '';
    },
    currentStepIcon() {
      const s = this.currentStep;
      if (!s) return 'mdi-navigation';
      return maneuverIcon(s.turnType, s.kind);
    },
    distanceToCurrentStepM() {
      if (!this.currentStep || !this.navigationActive) return 0;
      const target = this.currentStep.distanceAlongRouteM;
      return Math.max(0, target - this.progressAlongRouteM);
    },
    offRoute() {
      return this.navigationActive && this.crossTrackDistanceM > OFF_ROUTE_M;
    },
    showSimPanel() {
      return (IsDevEnv || this.devSimEnabled) && this.navPolyline.length >= 2;
    },
    simDistanceLabel() {
      const total = polylineTotalLengthM(this.navPolyline);
      const cur = (total * this.simProgressPct) / 100;
      return `${(cur / 1000).toFixed(2)} / ${(total / 1000).toFixed(2)} km`;
    },
    routeNavFingerprint() {
      return (this.customRoute || [])
        .map((p) => `${Number(p.lat)},${Number(p.lng)}`)
        .join('|');
    },
    stopsNavFingerprint() {
      return (this.stops || [])
        .map((s) => `${s.cod_parada || ''}:${Number(s.lat_parada)},${Number(s.lng_parada)}`)
        .join('|');
    },
  },
  data() {
    return {
      map: null,
      routeLine: null,
      remainingLine: null,
      stopMarkersLayer: null,
      userMarker: null,
      vehicleMarker: null,
      watchId: null,
      userCoords: null,
      speed: 0,
      userBearing: 0,
      routeInstructions: [],
      navPolyline: [],
      simplifiedRouteWarning: false,
      currentStepIndex: 0,
      progressAlongRouteM: 0,
      crossTrackDistanceM: 0,
      showAllSteps: false,
      userPannedAway: false,
      suspendGpsPanUntil: 0,
      wakeLock: null,
      prevUserCoords: null,
      gpsSimActive: false,
      simPlaying: false,
      simSpeedKmh: 30,
      simProgressPct: 0,
      routeSimulator: null,
      lastNavFingerprint: '',
      devSimEnabled:
        typeof window !== 'undefined' &&
        (window.location.search.indexOf('simulateNav=1') !== -1 ||
          window.localStorage.getItem('merustracker_simulate_nav') === '1'),
    };
  },
  watch: {
    routeNavFingerprint() {
      this.rebuildNavigation();
    },
    stopsNavFingerprint() {
      this.rebuildNavigation();
    },
    navigationActive(active) {
      if (active) {
        this.showAllSteps = false;
        this.requestWakeLock();
        this.updateRemainingRoute();
      } else {
        this.releaseWakeLock();
        this.drawFullRoute();
      }
      this.$nextTick(() => {
        if (this.map) this.map.invalidateSize();
      });
    },
    vehiclePosition: {
      handler(pos) {
        this.updateVehicleMarker(pos);
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => this.initMap());
  },
  beforeDestroy() {
    this.teardownMap();
  },
  methods: {
    initMap() {
      const el = this.$refs.mapCanvas;
      if (!el) return;

      this.map = L.map(el, {
        zoom: 15,
        zoomSnap: 0.5,
        center: this.mapDefaultCenter,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(this.map);

      this.userMarker = this.createUserMarker(this.mapDefaultCenter);
      this.userMarker.addTo(this.map);

      this.map.on('dragstart', () => {
        if (this.navigationActive) this.userPannedAway = true;
      });

      this.rebuildNavigation(true);

      this.map.whenReady(() => {
        if (this.map) {
          this.map.invalidateSize();
          this.ensureRouteSimulator();
          if (this.navPolyline.length >= 1 && this.userMarker) {
            const p = this.navPolyline[0];
            this.onPositionUpdate(p.lat, p.lng, 0, 0);
          }
        }
      });

      if (IsDevEnv && typeof window !== 'undefined') {
        window.__merustrackerNavSim = {
          play: () => this.toggleSimPlay(true),
          pause: () => this.pauseSim(),
          reset: () => this.resetSim(),
          seek: (pct) => this.onSimSeek(Number(pct)),
          setSpeed: (kmh) => {
            this.simSpeedKmh = Number(kmh) || 30;
            this.onSimSpeedChange();
          },
        };
      }

      if (!this.gpsSimActive) {
        this.startTracking();
      }
    },

    createUserMarker(latlng) {
      return L.circleMarker(latlng, {
        radius: 10,
        color: '#0F9D58',
        weight: 2,
        fillColor: '#81C784',
        fillOpacity: 0.95,
      });
    },

    rebuildNavigation(force) {
      const fingerprint = `${this.routeNavFingerprint}::${this.stopsNavFingerprint}`;

      if (
        !force &&
        fingerprint === this.lastNavFingerprint &&
        this.navPolyline.length >= 2 &&
        this.routeSimulator
      ) {
        return;
      }

      if (this.simPlaying) {
        this.pauseSim();
      }

      const plan = buildNavigationPlan(this.customRoute, this.stops);
      this.navPolyline = plan.polyline;
      this.routeInstructions = plan.instructions;
      this.simplifiedRouteWarning = plan.simplifiedRouteWarning;
      this.currentStepIndex = this.routeInstructions.length ? 0 : 0;

      this.initRouteSimulator();

      if (!this.map) {
        return;
      }

      if (this.navPolyline.length >= 2) {
        if (this.navigationActive) {
          this.updateRemainingRoute();
        } else {
          this.drawFullRoute();
        }
        try {
          const bounds = L.latLngBounds(this.navPolyline.map((p) => [p.lat, p.lng]));
          if (bounds.isValid()) {
            this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
          }
        } catch (e) {
          /* ignore */
        }
      } else if (this.navPolyline.length === 1) {
        this.map.setView([this.navPolyline[0].lat, this.navPolyline[0].lng], 16);
      } else {
        this.map.setView(this.mapDefaultCenter, 15);
      }

      this.updateStopMarkers();

      this.lastNavFingerprint = fingerprint;

      this.$nextTick(() => {
        if (this.map) this.map.invalidateSize();
      });
    },

    ensureRouteSimulator() {
      if (!IsDevEnv && !this.devSimEnabled) return;
      if (this.navPolyline.length < 2) return;
      if (!this.routeSimulator) {
        this.initRouteSimulator();
      }
    },

    initRouteSimulator() {
      if (this.routeSimulator) {
        this.routeSimulator.destroy();
        this.routeSimulator = null;
      }
      if (!IsDevEnv && !this.devSimEnabled) return;
      if (this.navPolyline.length < 2) return;

      this.routeSimulator = createRouteNavigationSimulator(this.navPolyline, {
        speedKmh: this.simSpeedKmh,
        tickMs: 400,
        onTick: (sample) => {
          this.simProgressPct = sample.progressPct;
          this.onPositionUpdate(sample.lat, sample.lng, sample.speedMs, sample.heading);
        },
        onComplete: () => {
          this.simPlaying = false;
        },
      });
    },

    toggleSimPlay(forcePlay) {
      if (forcePlay != null && forcePlay !== true) {
        forcePlay = undefined;
      }
      this.ensureRouteSimulator();
      if (!this.routeSimulator) {
        console.warn('[MapaMotorista] Simulador indisponível — rota com menos de 2 pontos ou ambiente não-dev.');
        return;
      }

      const shouldPlay = forcePlay === true || !this.simPlaying;
      if (shouldPlay) {
        this.stopGpsWatch();
        this.gpsSimActive = true;
        this.simPlaying = true;
        this.userPannedAway = false;
        this.routeSimulator.setSpeedKmh(this.simSpeedKmh);
        const started = this.routeSimulator.start(
          (polylineTotalLengthM(this.navPolyline) * this.simProgressPct) / 100,
        );
        if (!started) {
          this.simPlaying = false;
          console.warn('[MapaMotorista] Falha ao iniciar simulação GPS.');
        }
      } else {
        this.pauseSim();
      }
    },

    pauseSim() {
      this.simPlaying = false;
      if (this.routeSimulator) this.routeSimulator.pause();
    },

    resetSim() {
      this.pauseSim();
      this.simProgressPct = 0;
      if (this.routeSimulator) this.routeSimulator.reset();
      if (this.gpsSimActive && this.navPolyline.length) {
        const p = this.navPolyline[0];
        this.onPositionUpdate(p.lat, p.lng, 0, 0);
      }
    },

    onSimSpeedChange() {
      if (this.routeSimulator) this.routeSimulator.setSpeedKmh(this.simSpeedKmh);
    },

    onSimSeek(pct) {
      this.simProgressPct = Number(pct) || 0;
      if (this.routeSimulator) {
        this.routeSimulator.jumpTo(this.simProgressPct, true);
        const sample = this.routeSimulator.sample();
        this.onPositionUpdate(sample.lat, sample.lng, 0, sample.heading);
      }
    },

    stopGpsWatch() {
      if (this.watchId != null) {
        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
      }
    },

    createStopMarkerIcon(label) {
      const text = label != null && String(label).trim() !== '' ? String(label) : '•';
      return L.divIcon({
        className: 'nav-stop-divicon',
        html: `<div class="nav-stop-pin" aria-hidden="true"><span>${text}</span></div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
        tooltipAnchor: [0, -14],
      });
    },

    stopTooltip(stop, idx) {
      if (!stop) return '';
      const cod =
        stop.cod_parada != null && String(stop.cod_parada).trim() !== ''
          ? String(stop.cod_parada)
          : String(idx + 1);
      const addr = stop.endereco_parada ? String(stop.endereco_parada) : '';
      const sentido = stop.descricao_sentido ? String(stop.descricao_sentido) : '';
      return [cod, sentido, addr].filter((x) => x && x.trim() !== '').join(' · ');
    },

    updateStopMarkers() {
      if (!this.map) return;

      if (this.stopMarkersLayer) {
        try {
          this.map.removeLayer(this.stopMarkersLayer);
        } catch (e) {
          /* ignore */
        }
        this.stopMarkersLayer = null;
      }

      const stops = Array.isArray(this.stops) ? this.stops : [];
      if (!stops.length) return;

      const markers = [];
      stops.forEach((stop, idx) => {
        const lat = Number(stop && stop.lat_parada);
        const lng = Number(stop && stop.lng_parada);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const label =
          stop.cod_parada != null && String(stop.cod_parada).trim() !== ''
            ? stop.cod_parada
            : idx + 1;
        const marker = L.marker([lat, lng], {
          icon: this.createStopMarkerIcon(label),
          zIndexOffset: 200,
        });
        marker.bindTooltip(this.stopTooltip(stop, idx), {
          direction: 'top',
          offset: [0, -10],
          opacity: 0.95,
        });
        markers.push(marker);
      });

      if (!markers.length) return;

      this.stopMarkersLayer = L.layerGroup(markers).addTo(this.map);
    },

    drawFullRoute() {
      if (!this.map || this.navPolyline.length < 2) return;
      this.removeLayer(this.remainingLine);
      this.remainingLine = null;

      if (this.routeLine) {
        try {
          this.map.removeLayer(this.routeLine);
        } catch (e) {
          /* ignore */
        }
      }
      this.routeLine = L.polyline(
        this.navPolyline.map((p) => [p.lat, p.lng]),
        { color: '#4285F4', weight: 6, opacity: 0.95 },
      ).addTo(this.map);
    },

    updateRemainingRoute() {
      if (!this.map || this.navPolyline.length < 2) return;

      this.removeLayer(this.routeLine);
      this.routeLine = null;

      const remaining = getRemainingPolyline(this.navPolyline, this.progressAlongRouteM);
      const traveled = this.navPolyline.slice(0, 2);

      if (this.remainingLine) {
        try {
          this.map.removeLayer(this.remainingLine);
        } catch (e) {
          /* ignore */
        }
      }

      if (remaining.length >= 2) {
        this.remainingLine = L.polyline(
          remaining.map((p) => [p.lat, p.lng]),
          { color: '#1a73e8', weight: 7, opacity: 1 },
        ).addTo(this.map);
      }

      if (traveled.length >= 2 && this.progressAlongRouteM > 0) {
        this.routeLine = L.polyline(
          this.navPolyline.map((p) => [p.lat, p.lng]),
          { color: '#9aa0a6', weight: 5, opacity: 0.5, dashArray: '6 8' },
        ).addTo(this.map);
      }
    },

    removeLayer(layer) {
      if (layer && this.map) {
        try {
          this.map.removeLayer(layer);
        } catch (e) {
          /* ignore */
        }
      }
    },

    stepHasMapPoint(step) {
      return step && Number.isFinite(Number(step.lat)) && Number.isFinite(Number(step.lng));
    },

    stepText(step) {
      if (!step) return '';
      if (step.kind === 'stop' && step.stopLabel) {
        return this.$t('guiasMotorista.navNextStop', { name: step.stopLabel });
      }
      if (step.kind === 'arrive') return this.$t('guiasMotorista.navArrive');
      if (step.kind === 'start') return this.$t('guiasMotorista.navStart');
      if (step.kind === 'turn') {
        const key = `guiasMotorista.navTurn_${step.turnType || 'continue'}`;
        const translated = this.$t(key);
        if (translated !== key) return translated;
      }
      if (step.distanceToNextM > 0) {
        return this.$t('guiasMotorista.navContinueFor', {
          distance: this.formatDistance(step.distanceToNextM),
        });
      }
      return this.$t('guiasMotorista.navContinue');
    },

    stepIcon(step) {
      if (!step) return 'mdi-navigation';
      return maneuverIcon(step.turnType, step.kind);
    },

    formatDistance(meters) {
      return formatDistanceMeters(
        meters,
        this.$t('guiasMotorista.navKm'),
        this.$t('guiasMotorista.navM'),
      );
    },

    focusInstructionStep(step, index) {
      if (!this.stepHasMapPoint(step) || !this.map) return;
      this.currentStepIndex = index;
      this.suspendGpsPanUntil = Date.now() + 18000;
      const ll = L.latLng(Number(step.lat), Number(step.lng));
      const z = Math.max(this.map.getZoom(), 16);
      if (this.map.flyTo) {
        this.map.flyTo(ll, z, { duration: 0.55 });
      } else {
        this.map.setView(ll, z);
      }
    },

    recenterMap() {
      this.userPannedAway = false;
      this.suspendGpsPanUntil = 0;
      if (this.userCoords && this.map) {
        this.map.panTo([this.userCoords.lat, this.userCoords.lng]);
      }
    },

    updateVehicleMarker(pos) {
      if (!this.map) return;
      const lat = Number(pos && pos.lat);
      const lng = Number(pos && pos.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
        this.removeLayer(this.vehicleMarker);
        this.vehicleMarker = null;
        return;
      }
      const ll = [lat, lng];
      if (!this.vehicleMarker) {
        this.vehicleMarker = L.circleMarker(ll, {
          radius: 8,
          color: '#1565C0',
          weight: 2,
          fillColor: '#42A5F5',
          fillOpacity: 0.9,
        }).addTo(this.map);
      } else {
        this.vehicleMarker.setLatLng(ll);
      }
    },

    onPositionUpdate(lat, lng, speedMs, heading) {
      this.userCoords = { lat, lng };
      this.speed = speedMs ? (speedMs * 3.6).toFixed(0) : 0;

      if (this.prevUserCoords) {
        this.userBearing = this.computeBearing(this.prevUserCoords, { lat, lng });
      } else if (Number.isFinite(heading) && heading >= 0) {
        this.userBearing = heading;
      }
      this.prevUserCoords = { lat, lng };

      let displayLat = lat;
      let displayLng = lng;

      if (this.navPolyline.length >= 2) {
        const proj = projectPointOnPolyline({ lat, lng }, this.navPolyline);
        this.progressAlongRouteM = proj.distanceAlongRouteM;
        this.crossTrackDistanceM = proj.crossTrackDistanceM;

        if (this.navigationActive) {
          this.currentStepIndex = findCurrentStepIndex(
            this.routeInstructions,
            this.progressAlongRouteM,
          );
          this.updateRemainingRoute();

          if (proj.crossTrackDistanceM <= SNAP_TO_LINE_M) {
            displayLat = proj.snappedLat;
            displayLng = proj.snappedLng;
          }
        }
      }

      const ll = L.latLng(displayLat, displayLng);
      if (this.userMarker) {
        this.userMarker.setLatLng(ll);
      }

      if (
        this.map &&
        (this.navigationActive || (this.gpsSimActive && this.simPlaying)) &&
        !this.userPannedAway &&
        Date.now() >= (this.suspendGpsPanUntil || 0)
      ) {
        const z = Math.max(this.map.getZoom(), 17);
        this.map.setView(ll, z, { animate: true });
      }
    },

    computeBearing(from, to) {
      const φ1 = (from.lat * Math.PI) / 180;
      const φ2 = (to.lat * Math.PI) / 180;
      const dλ = ((to.lng - from.lng) * Math.PI) / 180;
      const y = Math.sin(dλ) * Math.cos(φ2);
      const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
      return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    },

    startTracking() {
      if (!navigator.geolocation || this.gpsSimActive) return;

      const options = {
        enableHighAccuracy: true,
        maximumAge: 1000,
      };

      this.watchId = navigator.geolocation.watchPosition(
        (position) => {
          if (this.gpsSimActive) return;
          const { latitude, longitude, speed, heading } = position.coords;
          this.onPositionUpdate(latitude, longitude, speed, heading);
        },
        (error) => console.error('Erro no GPS:', error),
        options,
      );
    },

    async requestWakeLock() {
      try {
        if (typeof navigator !== 'undefined' && navigator.wakeLock) {
          this.wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (e) {
        /* ignore */
      }
    },

    releaseWakeLock() {
      if (this.wakeLock) {
        try {
          this.wakeLock.release();
        } catch (e) {
          /* ignore */
        }
        this.wakeLock = null;
      }
    },

    teardownMap() {
      this.releaseWakeLock();
      this.pauseSim();
      if (this.routeSimulator) {
        this.routeSimulator.destroy();
        this.routeSimulator = null;
      }
      if (typeof window !== 'undefined' && window.__merustrackerNavSim) {
        delete window.__merustrackerNavSim;
      }
      this.stopGpsWatch();
      if (this.map) {
        this.removeLayer(this.routeLine);
        this.removeLayer(this.remainingLine);
        this.removeLayer(this.stopMarkersLayer);
        this.removeLayer(this.userMarker);
        this.removeLayer(this.vehicleMarker);
        this.routeLine = null;
        this.remainingLine = null;
        this.stopMarkersLayer = null;
        this.userMarker = null;
        this.vehicleMarker = null;
        this.map.remove();
        this.map = null;
      }
    },
  },
};
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  min-height: 200px;
}

.map-container--nav .map-canvas {
  height: 100%;
}

.map-canvas {
  width: 100%;
  height: 100%;
}

/* Leaflet zoom defaults to top-left (z-index 800); keep nav UI above it and out of the way */
.map-container:not(.map-container--nav) :deep(.leaflet-top.leaflet-left) {
  left: auto;
  right: 10px;
}

.map-container--nav :deep(.leaflet-top.leaflet-left) {
  top: auto;
  bottom: max(56px, calc(env(safe-area-inset-bottom) + 48px));
  left: 10px;
}

.nav-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  padding-top: max(12px, env(safe-area-inset-top));
  background: linear-gradient(135deg, #1a73e8 0%, #1557b0 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.nav-banner-icon {
  flex-shrink: 0;
}

.nav-banner-text {
  flex: 1;
  min-width: 0;
}

.nav-banner-distance {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.nav-banner-instruction {
  font-size: 0.95rem;
  line-height: 1.3;
  opacity: 0.95;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.off-route-panel {
  position: absolute;
  top: 72px;
  left: 10px;
  right: 10px;
  z-index: 1100;
  background: #fce8e6;
  color: #c5221f;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.speed-chip {
  position: absolute;
  top: max(72px, calc(env(safe-area-inset-top) + 60px));
  left: 10px;
  z-index: 1100;
  background: rgba(255, 255, 255, 0.92);
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.recenter-fab {
  position: absolute !important;
  bottom: max(80px, calc(env(safe-area-inset-bottom) + 64px));
  right: 12px;
  z-index: 1100;
}

.all-steps-btn {
  position: absolute !important;
  bottom: max(16px, env(safe-area-inset-bottom));
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  background: rgba(26, 115, 232, 0.85) !important;
}

.info-panel {
  position: absolute;
  top: 10px;
  left: 10px;
  background: white;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  z-index: 1100;
}

.notice-panel {
  position: absolute;
  top: 52px;
  left: 10px;
  right: 10px;
  max-width: 420px;
  background: #fff8e1;
  color: #5d4037;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 1100;
}

.instructions-panel {
  position: absolute;
  right: 10px;
  bottom: 10px;
  left: 10px;
  max-height: 38%;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.97);
  padding: 12px 14px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  z-index: 1100;
}

.instructions-panel--nav {
  max-height: 45%;
  bottom: max(16px, env(safe-area-inset-bottom));
}

@media (min-width: 640px) {
  .instructions-panel {
    left: auto;
    width: min(360px, 42vw);
    max-height: 55%;
  }

  .notice-panel {
    right: auto;
  }
}

.instructions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.instructions-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.instructions-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
  font-size: 0.875rem;
  line-height: 1.35;
}

.instruction-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 6px;
  padding: 4px 2px;
  border-radius: 6px;
}

.instruction-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.instruction-row--clickable {
  cursor: pointer;
}

.instruction-row--clickable:hover {
  background: rgba(66, 133, 244, 0.08);
}

.instruction-row--active {
  background: rgba(66, 133, 244, 0.14);
}

.instruction-text {
  flex: 1;
}

.instruction-meta {
  color: #5f6368;
  font-size: 0.8rem;
  white-space: nowrap;
}

.sim-panel {
  position: absolute;
  left: 10px;
  bottom: max(16px, env(safe-area-inset-bottom));
  z-index: 650;
  width: min(280px, calc(100vw - 20px));
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(33, 33, 33, 0.92);
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

.map-container--nav .sim-panel {
  bottom: max(72px, calc(env(safe-area-inset-bottom) + 56px));
}

.sim-panel-title {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.85;
  margin-bottom: 4px;
}

.sim-panel-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;
}

.sim-panel-speed {
  margin-left: auto;
  font-size: 0.75rem;
  opacity: 0.9;
}

.sim-slider {
  margin: 0;
  padding: 0 4px;
}

.sim-panel-hint {
  font-size: 0.7rem;
  opacity: 0.75;
  text-align: center;
  margin-top: 2px;
}

:deep(.nav-stop-divicon) {
  background: transparent;
  border: none;
}

:deep(.nav-stop-pin) {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fc6262;
  border: 2px solid #1a73e8;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
</style>
